/**
 * Selection interaction system.
 *
 * Click selects a node (updates context panel).
 * Double-click dives deeper through the depth system.
 * Escape is now handled by the depth navigation system (zone.ts).
 */

import * as THREE from 'three';
import { FieldCamera } from '../scene/camera';
import { NodeRenderer } from '../scene/nodes';
import { useAuraStore } from '../state/store';
import type { SyntheticField } from '../data/synthetic';
import type { ZoneNavigation } from '../scene/zone';

const FLAG_SELECTED = 4; // Bit flag

export class SelectionSystem {
  private boundPositions: THREE.Vector3[] = [];

  constructor(
    private container: HTMLElement,
    private camera: THREE.PerspectiveCamera,
    private fieldCamera: FieldCamera,
    private nodeRenderer: NodeRenderer,
    private field: SyntheticField,
    private zoneNavigation: ZoneNavigation,
  ) {
    // Pre-compute positions
    this.boundPositions = field.nodes.map((n) =>
      new THREE.Vector3(...n.position),
    );

    // Click to select
    container.addEventListener('click', (e) => {
      const store = useAuraStore.getState();
      if (!store.entryCompleted) return;

      // If something is hovered, select it
      if (store.hoveredNodeIndex >= 0) {
        this.selectNode(store.hoveredNodeIndex);
      }
    });

    // Double-click to dive deeper through depth system
    container.addEventListener('dblclick', (e) => {
      const store = useAuraStore.getState();
      if (store.hoveredNodeIndex >= 0) {
        this.zoneNavigation.focusNode(store.hoveredNodeIndex);
      }
    });

    // Escape is handled by ZoneNavigation depth system
  }

  private selectNode(index: number) {
    const store = useAuraStore.getState();
    const node = this.field.nodes[index];
    if (!node) return;

    // Clear previous selection
    if (store.selectedNodeIndex >= 0) {
      this.nodeRenderer.setNodeFlag(store.selectedNodeIndex, 0);
    }

    // Mark as selected
    this.nodeRenderer.setNodeFlag(index, FLAG_SELECTED);

    // Update store
    store.setSelectedNode(index, {
      id: node.id,
      name: node.name,
      category: node.category,
      signalStrength: node.intensity,
      trend: node.trend,
      nodeIndex: index,
    });
  }

  private deselect() {
    const store = useAuraStore.getState();
    if (store.selectedNodeIndex >= 0) {
      this.nodeRenderer.setNodeFlag(store.selectedNodeIndex, 0);
    }
    store.clearSelection();
  }

  dispose() {}
}
